import { HttpClient } from '@adapters/HttpClient';
import {
  CreatePackLinkRequest,
  CreatePackRequest,
  Pack,
  PackFilters,
  PackPage,
  PacksApiResponse,
  Product,
} from '../models/Pack';
import { UpdatePackRequest } from '../services/PackService';

export class PackApiAdapter {
  constructor(private httpClient: HttpClient) {}

  // 🎯 Función para normalizar el pack: mapear liked_packs/Liked_packs a linked_packs
  private normalizePack(pack: any): Pack {
    // Evita errores cuando la API responde sin contenido (ej: 204 No Content)
    if (!pack || typeof pack !== 'object') {
      return pack as Pack;
    }

    // La API puede devolver 'liked_packs' o 'Liked_packs' pero el modelo espera 'linked_packs'
    const likedPacks = pack.liked_packs || pack.Liked_packs;
    if (likedPacks && !pack.linked_packs) {
      pack.linked_packs = likedPacks;
      delete pack.liked_packs;
      delete pack.Liked_packs;
    }
    return pack as Pack;
  }

  // 🎯 Método original para compatibilidad hacia atrás
  async getPacks(filters: PackFilters = {}): Promise<Pack[]> {
    const response = await this.getPacksPaginated(filters);
    return response.packs;
  }

  // 🎯 Nuevo método para scroll infinito - retorna respuesta completa
  async getPacksPaginated(filters: PackFilters = {}): Promise<PackPage> {
    const { searchTerm = '', teamId, productId, sortBy, sortDirection, page = 1, limit = 10 } = filters;

    const params: any = {
      ...(searchTerm && searchTerm.trim().length > 0 && { title: searchTerm.trim() }),
      ...(teamId && { team_id: teamId }),
      ...(productId && { product_id: productId }),
      // 🎯 Parámetros de sorting
      ...(sortBy && { sorting_column: sortBy }),
      ...(sortDirection && { sorting_direction: sortDirection }),
      page,
      per_page: limit,
    };

    // 🎯 Lógica simple: mostrar barra global solo en página 1
    const showGlobalLoading = page === 1;

    const response = await this.httpClient.get<PacksApiResponse>(
      '/packs',
      params,
      {}, // config
      showGlobalLoading,
    );

    // 🎯 Normalizar los packs: mapear liked_packs a linked_packs
    const normalizedPacks = response.data.map((pack) => this.normalizePack(pack));

    // 🎯 Transformar respuesta API a formato para useInfiniteQuery
    return {
      packs: normalizedPacks,
      nextPage: response.meta.currentPage < response.meta.lastPage ? response.meta.currentPage + 1 : undefined,
      hasMore: response.meta.currentPage < response.meta.lastPage,
      total: response.meta.total,
    };
  }

  // 🎯 Obtener productos para agregar al pack
  async getProducts(): Promise<Product[]> {
    const response = await this.httpClient.get<{ data: Product[] }>('/products', {
      fields: 'basic',
      per_page: 10000,
    });

    return response.data || [];
  }

  // 🎯 Obtener un pack específico por ID
  async getPackById(id: number): Promise<Pack> {
    const response = await this.httpClient.get<Pack>(`/packs/${id}`);
    // 🎯 Normalizar el pack: mapear liked_packs a linked_packs
    return this.normalizePack(response);
  }

  // 🎯 Crear nuevo pack
  async createPack(data: CreatePackRequest): Promise<Pack> {
    const response = await this.httpClient.post<Pack>('/packs', data);
    // 🎯 Normalizar el pack: mapear liked_packs a linked_packs
    return this.normalizePack(response);
  }

  // 🎯 Actualizar pack existente
  async updatePack(id: number, data: UpdatePackRequest): Promise<Pack> {
    const response = await this.httpClient.put<Pack | null>(`/packs/${id}`, data);

    // Si el backend responde 204 No Content, pedimos el pack actualizado.
    if (!response) {
      return this.getPackById(id);
    }

    // 🎯 Normalizar el pack: mapear liked_packs a linked_packs
    return this.normalizePack(response);
  }

  // 🎯 Eliminar pack
  async deletePack(id: number): Promise<void> {
    await this.httpClient.delete(`/packs/${id}`);
  }

  // 🎯 Crear enlace de pack con tienda externa
  async createLink(data: CreatePackLinkRequest): Promise<any> {
    const response = await this.httpClient.post<any>('/packs/link', data);
    return response;
  }

  /**
   * Vincula el pack con un SKU de Amazon (FBM).
   * POST /packs/external/link/amazon-sku
   */
  async linkAmazonSku({
    packId,
    externalVariantId,
    shopId,
  }: {
    packId: number;
    externalVariantId: string;
    shopId: number;
  }): Promise<any> {
    return this.httpClient.post<any>('/packs/external/link/amazon-sku', {
      pack_id: packId,
      external_variant_id: externalVariantId,
      shop_id: shopId,
    });
  }

  // 🎯 Eliminar enlace de pack
  async deleteLink(linkId: number): Promise<void> {
    await this.httpClient.delete(`/packs/external/link/${linkId}`);
  }
}
